import React, { useEffect } from "react";
import dayjs from "dayjs";
import ReactDatePicker from "react-datepicker";
import { BOLD_TEXT, COLOR_TEXT, SIZE_TEXT } from "@/comn/features/foundation";
import { comnUtils } from "@/comn/utils";
import { useTheme } from "@/comn/hooks";
import { Icon } from "@/comn/components";
import constants from "@/comn/constants";
import classNames from "classnames";

export type InputDateimeProps = {
    edit?: boolean;
    mode?: "edit" | "view" | null;
    maxDate?: Date;
    minDate?: Date;
    startRef?: any;
    endRef?: any;

    name?: string;
    value?: any;
    readOnly?: boolean;
    disabled?: boolean;
    defaultValue?: any;
    second?: boolean;

    color?: keyof typeof COLOR_TEXT;
    editColor?: keyof typeof COLOR_TEXT;
    bold?: keyof typeof BOLD_TEXT;
    editBold?: keyof typeof BOLD_TEXT;
    fontSize?: keyof typeof SIZE_TEXT;

    onBlur?: (arg?: any) => void;
    onChange?: (arg?: any) => void;
};

export const InputDatetime = React.forwardRef((props: InputDateimeProps, ref: any) => {
    const {
        edit = true,
        mode,
        startRef,
        endRef,
        maxDate = new Date("9999-12-31"),
        minDate = new Date("1800-01-01"),
        /** input props */
        name,
        value,
        readOnly,
        disabled,
        defaultValue,
        second = false,

        color,
        editColor,
        bold,
        editBold,
        fontSize,

        onBlur,
        onChange,
    } = props;
    const _props = Object.fromEntries(
        Object.entries({
            maxDate,
            minDate,
            name,
            readOnly,
            disabled,
            defaultValue,
            onBlur,
        }).filter(([, value]) => value !== undefined),
    );

    const { theme } = useTheme();

    const handleChange = (date: Date) => {
        if (onChange) {
            onChange(unformatDatetime(date) || null);
        }
    };

    useEffect(() => {
        if (startRef) startRef.current.handleChangeStart = handleChange;
        if (endRef) endRef.current.handleChangeEnd = handleChange;
    }, []);

    const EDIT_STATUS = mode === "edit" ? true : mode === "view" ? false : edit;
    const _value = formatDatetime(value);

    return (
        <div className="w-full">
            {!EDIT_STATUS && (
                <div
                    className={classNames(
                        "control-text w-fit min-w-max",
                        comnUtils.getViewStyle(color, bold, fontSize),
                    )}
                >
                    {second === true ? localeDatetimeSecond(value, theme.lang) : localeDatetime(value, theme.lang)}
                </div>
            )}
            <div hidden={!EDIT_STATUS}>
                <div className="relative w-full [&>div]:w-full">
                    <Icon icon="calendar" size="xs" className="absolute left-1 top-1/2 -translate-y-1/2 z-[1]" />
                    <ReactDatePicker
                        {..._props}
                        ref={(context: any) => {
                            if (context) {
                                if (typeof ref === "function") {
                                    ref(context.input);
                                }
                            }
                        }}
                        selected={_value}
                        onChange={handleChange}
                        dateFormat={constants.DATETIME_FORMAT_INPUT[theme.lang]}
                        autoComplete="off"
                        showTimeSelect
                        timeIntervals={5}
                        className={"input pl-5" + comnUtils.getEditStyle(editColor, editBold)}
                        portalId="datepicker"
                        popperProps={{ strategy: "fixed" }}
                        showYearDropdown={true}
                        scrollableYearDropdown={true}
                        yearDropdownItemNumber={80}
                        dropdownMode="scroll"
                        showMonthDropdown={true}
                    />
                </div>
            </div>
        </div>
    );
});

export const localeDatetime = (v: any, l: "ko" | "en" | "tz") => {
    if (!v) return "";
    if (!dayjs(v).isValid()) return "";

    return dayjs(v).format(constants.DATETIME_FORMAT_DAYJS[l]);
};

export const localeDatetimeSecond = (v: any, l: "ko" | "en" | "tz") => {
    if (!v) return "";
    if (!dayjs(v).isValid()) return "";

    return dayjs(v).format(constants.DATETIMESECOND_FORMAT_DAYJS[l]);
};

export const formatDatetime = (v: any) => {
    if (!v) return null;
    if (!dayjs(v).isValid()) return null;

    return dayjs(v).toDate();
};

export const unformatDatetime = (v: any, o?: any) => {
    if (!v) return undefined;
    if (!dayjs(v).isValid()) return undefined;

    return dayjs(v).format(constants.DATETIME_FORMAT);
};
